import { useRef, useState } from "react";
import {
  motion,
  type MotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
} from "motion/react";
import { whyUsCards } from "@/config/why-us.config";
import { useHardwareAcceleration } from "@/hooks/use-hardware-acceleration";
import SegmentedProgress from "../ui/segmented-progress";
import { whyUsProofSummaries } from "../ui/why-us-summary";
import { cn } from "@/lib/utils";

interface MobileReasonSceneProps {
  activeReason: number;
  index: number;
  motionDisabled: boolean;
  progress: MotionValue<number>;
}

function MobileReasonScene({
  activeReason,
  index,
  motionDisabled,
  progress,
}: MobileReasonSceneProps) {
  const card = whyUsCards[index];
  const opacityStops =
    index === 0
      ? [0, 0.24, 0.36]
      : index === whyUsCards.length - 1
        ? [0.64, 0.76, 1]
        : [0.27, 0.39, 0.61, 0.73];
  const opacityValues =
    index === 0
      ? [1, 1, 0]
      : index === whyUsCards.length - 1
        ? [0, 1, 1]
        : [0, 1, 1, 0];
  const yStops = opacityStops;
  const yValues =
    index === 0
      ? [0, 0, -24]
      : index === whyUsCards.length - 1
        ? [24, 0, 0]
        : [24, 0, 0, -24];
  const opacity = useTransform(progress, opacityStops, opacityValues);
  const y = useTransform(progress, yStops, yValues);

  return (
    <motion.div
      aria-hidden="true"
      style={motionDisabled ? undefined : { opacity, y }}
      className={`absolute inset-0 flex items-center justify-center px-6 pb-24 pt-16 ${
        motionDisabled && index !== activeReason ? "hidden" : ""
      }`}
    >
      <span
        aria-hidden="true"
        className="text-outline pointer-events-none absolute left-1/2 top-[44%] -translate-x-1/2 -translate-y-1/2 select-none text-[11rem] font-medium leading-none tracking-[-0.06em] opacity-35"
      >
        {String(index + 1).padStart(2, "0")}
      </span>
      <div className="relative w-full text-center">
        <span
          className="mx-auto mb-7 block h-px w-14 bg-linear-to-r from-[#149CFF] to-[#DEFF77]"
          aria-hidden="true"
        />
        <h2 className="text-[clamp(1.2rem,5.4vw,1.45rem)] font-light leading-[1.12] tracking-[-0.035em]">
          {card!.title}
        </h2>
        <p className="mx-auto mt-7 max-w-[330px] text-[clamp(0.82rem,3.65vw,0.95rem)] font-light leading-[1.72] text-white/62">
          {whyUsProofSummaries[index]}
        </p>
      </div>
    </motion.div>
  );
}

export default function MobileWhyUs() {
  const storyRef = useRef<HTMLElement>(null);
  const [activeReason, setActiveReason] = useState(0);
  const reduceMotion = useReducedMotion();
  const hasHardwareAcceleration = useHardwareAcceleration();
  const motionDisabled = Boolean(reduceMotion) || !hasHardwareAcceleration;
  const { scrollYProgress } = useScroll({
    target: storyRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (progress) => {
    const nextReason = Math.min(
      whyUsCards.length - 1,
      Math.floor(Math.min(0.999, Math.max(0, progress)) * whyUsCards.length)
    );

    setActiveReason((current) =>
      current === nextReason ? current : nextReason
    );
  });

  return (
    <section
      id="why-us"
      ref={storyRef}
      data-mobile-section="why-us"
      className="relative min-h-[285svh] scroll-mt-18"
    >
      <div className="sticky top-18 h-[calc(100svh-4.5rem)] touch-pan-y transform-gpu overflow-hidden [backface-visibility:hidden]">
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_46%,rgba(20,156,255,0.11),transparent_43%)]"
          aria-hidden="true"
        />

        {whyUsCards.map((card, index) => (
          <MobileReasonScene
            key={card.title}
            activeReason={activeReason}
            index={index}
            motionDisabled={motionDisabled}
            progress={scrollYProgress}
          />
        ))}

        <div className="absolute inset-x-4 bottom-5 flex h-8 items-center gap-3">
          <span className="min-w-13 font-mono text-[10px] text-white/48">
            {String(activeReason + 1).padStart(2, "0")} /{" "}
            {String(whyUsCards.length).padStart(2, "0")}
          </span>
          <SegmentedProgress
            activeIndex={activeReason}
            count={whyUsCards.length}
            label="Why Us chapter progress"
            progress={scrollYProgress}
          />
          <div className="flex min-w-14 items-center justify-end gap-1 text-[10px] text-white/52">
            <span>Scroll</span>
            <span
              className={cn(
                "text-sm leading-none text-white/65 [--nudge:3px]",
                !motionDisabled && "nudge-down"
              )}
              aria-hidden="true"
            >
              ↓
            </span>
          </div>
        </div>
      </div>

      <ol className="sr-only">
        {whyUsCards.map((card, index) => (
          <li key={card.title}>
            <h2>{card.title}</h2>
            <p>{whyUsProofSummaries[index]}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}
